import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  ChevronRight,
  Search,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { deliveryService } from '@services/delivery.service';
import type { DeliveryProviderInfo } from '@services/delivery.service';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';

// ── Helpers ───────────────────────────────────────────────────────────────────

const modeChip: Record<string, string> = {
  live:    'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  sandbox: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
};

const statusChip: Record<string, string> = {
  active:   'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  inactive: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

function providerInitial(info: DeliveryProviderInfo): string {
  return info.displayName.charAt(0).toUpperCase();
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const ProvidersListPage = () => {
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  // All providers with metadata + counts: drives summary cards and filter dropdown.
  const { data: providers = [] } = useQuery({
    queryKey: ['delivery', 'providers'],
    queryFn: () => deliveryService.listProviders(),
    staleTime: 60_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['delivery', 'third-party-configs', page, providerFilter, search],
    queryFn: () =>
      deliveryService.listThirdPartyConfigs({
        provider: providerFilter || undefined,
        page,
        limit: 20,
        search: search.trim() || undefined,
      }),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  // Build a slug → info map for use in table rows.
  const providerMap = new Map<string, DeliveryProviderInfo>(
    providers.map((p) => [p.slug, p]),
  );

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Page header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Delivery / Providers
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Third-Party Providers
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            All merchant-configured third-party delivery provider integrations
          </p>
        </div>

        {/* Provider summary cards: rendered from API data */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {providers.map((p) => (
            <Link
              key={p.slug}
              to={p.detailPath}
              className="rounded-3xl bg-white shadow-soft p-5 flex flex-col gap-3 hover:shadow-md transition-all group"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-[16px]"
                style={{ backgroundColor: p.uiColor }}
              >
                {providerInitial(p)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[13px] font-semibold text-[var(--text-primary)]">{p.displayName}</p>
                  {!p.isActive && (
                    <span className="rounded-full bg-[var(--state-danger-bg)] border border-[var(--state-danger-border)] text-[var(--state-danger)] text-[10px] font-semibold px-2 py-0.5">
                      Deactivated
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-[var(--text-muted)]">
                  {p.isDemoProvider
                    ? 'Sandbox only'
                    : `${p.merchantCount} merchant${p.merchantCount !== 1 ? 's' : ''}`}
                </p>
              </div>
              <span className="text-[11px] font-semibold text-[var(--text-accent)] group-hover:underline">
                View configs
              </span>
            </Link>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search business, branch..."
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          {/* Filter dropdown built from API data: no hardcoded options */}
          <select
            value={providerFilter}
            onChange={(e) => { setProviderFilter(e.target.value); setPage(1); }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
          >
            <option value="">All providers</option>
            {providers
              .filter((p) => !p.isDemoProvider)
              .map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.displayName}
                </option>
              ))}
          </select>
        </div>

        {/* Table */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {isLoading && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">Loading configurations...</div>
          )}

          {error && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load provider configurations.
            </div>
          )}

          {!isLoading && !error && (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Provider
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Business / Branch
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    API Mode
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Webhook
                  </th>
                  <th className="px-6 py-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {rows.map((r) => {
                  const info = providerMap.get(r.provider);
                  const label = info?.displayName ?? r.provider;
                  const color = info?.uiColor ?? 'var(--text-muted)';
                  const path  = info?.detailPath ?? `/delivery/providers/${r.provider}`;

                  return (
                    <tr key={r.id} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-bold text-[13px]"
                            style={{ backgroundColor: color }}
                          >
                            {label.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-[14px] font-medium text-[var(--text-primary)]">{label}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-[14px] font-medium text-[var(--text-primary)]">
                          {r.businessName || '—'}
                        </div>
                        <div className="text-[12px] text-[var(--text-muted)]">{r.branchName || '—'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                            modeChip[r.apiMode] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {r.apiMode}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                            r.isActive ? statusChip.active : statusChip.inactive,
                          )}
                        >
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {r.webhookConfigured ? (
                          <div className="flex items-center gap-1.5 text-[13px] text-[var(--state-success)]">
                            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                            Configured
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
                            <XCircle className="w-4 h-4" strokeWidth={1.5} />
                            Not set
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={path}
                          className="w-8 h-8 rounded-full inline-flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <Truck className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
                      <p className="text-[14px] text-[var(--text-muted)]">
                        {search || providerFilter
                          ? 'No configurations match your filters.'
                          : 'No third-party provider configurations yet.'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-[var(--text-muted)]">
              Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-[13px] text-[var(--text-muted)] px-2">
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </PageGuard>
  );
};
